import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useSEO } from '../../hooks/useSEO';
import { articlesApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Newspaper, Calendar, Eye, ChevronRight } from 'lucide-react';

export const BlogPage = () => {
  const { t, language } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { value: 'all', label: language === 'fr' ? 'Tous' : 'الكل' },
    { value: 'guide', label: t('blog_category_guide') },
    { value: 'actualite', label: t('blog_category_news') },
    { value: 'conseil', label: t('blog_category_advice') },
    { value: 'quartier', label: t('blog_category_neighborhood') },
  ];

  useEffect(() => {
    loadArticles();
  }, [selectedCategory]);

  const loadArticles = async () => {
    setLoading(true);
    try {
      const params = selectedCategory !== 'all' ? { category: selectedCategory } : {};
      const response = await articlesApi.list({ ...params, limit: 20 });
      setArticles(response.data || []);
    } catch (error) {
      console.error('Failed to load articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ar' ? 'ar-DZ' : 'fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const blogSeoTitle = language === 'fr' ? 'Blog immobilier - Guides, conseils et actualites | Beytam' : 'مدونة عقارية - أدلة ونصائح وأخبار | بيتم';
  const blogSeoDesc = language === 'fr' ? 'Retrouvez nos articles, guides et conseils pour votre projet immobilier en Algerie. Actualites du marche, prix, quartiers.' : 'اطلع على مقالاتنا وأدلتنا ونصائحنا لمشروعك العقاري في الجزائر.';

  useSEO({
    title: blogSeoTitle,
    description: blogSeoDesc,
    ogTitle: blogSeoTitle,
    canonical: window.location.href,
  });

  return (
    <div className="min-h-screen bg-background" data-testid="blog-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary-500 to-primary-900 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="font-manrope text-4xl md:text-5xl font-bold mb-4">
            {t('blog_title')}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            {t('blog_subtitle')}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Categories */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <Button
              key={cat.value}
              variant={selectedCategory === cat.value ? 'default' : 'outline'}
              className={selectedCategory === cat.value ? 'bg-primary-500' : ''}
              onClick={() => setSelectedCategory(cat.value)}
              data-testid={`category-${cat.value}`}
            >
              {cat.label}
            </Button>
          ))}
        </div>

        {/* Articles */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="space-y-4">
                <Skeleton className="aspect-video rounded-xl" />
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20">
            <Newspaper className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              {language === 'fr' ? 'Aucun article' : 'لا توجد مقالات'}
            </h3>
            <p className="text-slate-600">
              {language === 'fr' 
                ? 'Revenez bientôt pour de nouveaux contenus'
                : 'عد قريباً للمزيد من المحتوى'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article, index) => (
              <Link 
                key={article.id} 
                to={`/blog/${article.slug}`}
                className={index === 0 ? 'md:col-span-2 lg:col-span-2' : ''}
                data-testid={`article-card-${article.slug}`}
              >
                <Card className="overflow-hidden border-0 shadow-md hover:shadow-lg transition-all group h-full">
                  <div className={`${index === 0 ? 'aspect-[2/1]' : 'aspect-video'} overflow-hidden relative`}>
                    <img
                      src={article.image || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800'}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute top-4 left-4 flex gap-2">
                      <Badge className="bg-primary-500 text-white">
                        {article.category}
                      </Badge>
                      <Badge variant="secondary" className="bg-white/90">
                        {article.city}
                      </Badge>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className={`font-manrope font-bold text-slate-900 group-hover:text-primary-500 transition-colors mb-3 ${index === 0 ? 'text-2xl' : 'text-lg'} line-clamp-2`}>
                      {language === 'ar' && article.title_ar ? article.title_ar : article.title}
                    </h3>
                    <p className="text-slate-600 text-sm line-clamp-2 mb-4">
                      {language === 'ar' && article.excerpt_ar ? article.excerpt_ar : article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(article.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          {article.views}
                        </span>
                      </div>
                      <span className="text-primary-500 flex items-center gap-1 font-medium group-hover:gap-2 transition-all">
                        {t('blog_read_more')}
                        <ChevronRight className="w-4 h-4" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
